import { makeStyles } from '@material-ui/core'
import {
  LocationOnOutlined as LocationIcon,
  ThumbUpOutlined as ThumbUpIcon,
} from '@material-ui/icons'
import clsx from 'clsx'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import { flattenAddress } from 'src/utils'
import { ProductList, Toolbar, Typography } from 'src/modules/ui'

import AboutSupplier from './AboutSupplier'
import Agreement from './Agreement'
import SupplierActions from './SupplierActions'

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.fadedGreen.main,
    borderRadius: theme.shape.borderRadius,
  },
  actions: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('tablet')]: {
      marginBottom: 0,
      textAlign: 'right',
    },
  },
  address: {
    alignItems: 'center',
    color: 'white',
    display: 'flex',
    fontFamily: 'Gotham',
    fontWeight: 500,
  },
  body: {
    padding: theme.spacing(1, 4, 4),
  },
  header: {
    alignItems: 'center',
    backgroundColor: theme.palette.darkGreen.main,
    backgroundSize: 'cover',
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
    },
  },
  logo: {
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexShrink: 0,
    height: 120,
    justifyContent: 'center',
    marginRight: theme.spacing(4),
    objectFit: 'cover',
    textAlign: 'center',
    width: 120,
  },
  metaColumn: {
    flexShrink: 0,
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('tablet')]: {
      marginTop: 0,
    },
  },
  name: {
    color: 'white',
    fontFamily: 'Gotham',
    fontSize: '1.75rem',
    fontWeight: 500,
  },
  productTable: {
    maxWidth: 600,
    width: '100%',
  },
  reviewCount: {
    alignItems: 'center',
    color: 'white',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
  },
  reviewCountIcon: {
    backgroundColor: theme.palette.orange.main,
    borderRadius: '50%',
    color: 'white',
    padding: 4,
    marginRight: theme.spacing(1),
  },
  titleArea: {
    flexGrow: 1,
  },
}))

const SupplierProfile = ({
  canMessage,
  canApproveAgreement,
  canAddProducts,
  canRequestAgreement,
  canStar,
  canLeaveReview,
  className,
  linkPrefix,
  showPrices,
  ...supplier
}) => {
  const { agreement, coverImage, id, logo, name, registeredAddress, reviews } =
    supplier

  const proposalStatus = agreement && agreement.status

  const classes = useStyles()

  const tabs = [
    {
      label: 'About',
      exact: true,
      path: `${linkPrefix}/suppliers/${id}/about`,
    },
    {
      label: 'Product Catalogue',
      path: `${linkPrefix}/suppliers/${id}/products`,
    },
  ]
  const showAgreement = ['proposed', 'accepted'].includes(proposalStatus)
  if (showAgreement) {
    const statusMap = {
      proposed: 'Proposal',
      accepted: 'Agreement',
    }
    tabs.push({
      label: statusMap[proposalStatus],
      path: `${linkPrefix}/suppliers/${id}/agreement`,
    })
  }
  const redirect = showAgreement
    ? `${linkPrefix}/suppliers/${id}/agreement`
    : `${linkPrefix}/suppliers/${id}/about`

  const gradient =
    'linear-gradient(to bottom, rgba(0, 0, 0, 0.52), rgba(0, 0, 0, 0.73))'

  return (
    <div className={clsx([classes.root, className])}>
      <div
        className={classes.header}
        style={{
          backgroundImage: `${gradient}, url(${coverImage})`,
        }}
      >
        {logo ? (
          <img src={logo} alt={name} className={classes.logo} />
        ) : (
          <div className={classes.logo}>
            <Typography variant="body2" color="lightGreen">
              {name}
            </Typography>
          </div>
        )}
        <div className={classes.titleArea}>
          <Typography className={classes.name}>{name}</Typography>
          <Typography className={classes.address} variant="body2">
            <LocationIcon className={classes.addressIcon} />
            {flattenAddress(registeredAddress)}
          </Typography>
        </div>
        <div className={classes.metaColumn}>
          {reviews.length > 0 && (
            <Typography className={classes.reviewCount} variant="body2">
              <ThumbUpIcon className={classes.reviewCountIcon} />
              {pluralize('recommendation', reviews.length, true)}
            </Typography>
          )}
        </div>
      </div>
      <Toolbar>
        <Toolbar.Tabs tabs={tabs} />
        <SupplierActions
          className={classes.actions}
          {...{
            canMessage,
            canRequestAgreement,
            canStar,
            proposalStatus,
          }}
          {...supplier}
        />
      </Toolbar>

      <Switch>
        <Route path={`${linkPrefix}/suppliers/${id}/about`}>
          <AboutSupplier canLeaveReview={canLeaveReview} {...supplier} />
        </Route>
        <Route path={`${linkPrefix}/suppliers/${id}/products`}>
          <ProductList
            className={classes.body}
            linkPrefix={linkPrefix}
            products={supplier.products}
            showFilter={true}
            showPrices={showPrices}
            showSearch={true}
            tableClassName={classes.productTable}
          />
        </Route>
        <Route path={`${linkPrefix}/suppliers/${id}/agreement`}>
          <Agreement
            canAddProducts={canAddProducts}
            canApproveAgreement={canApproveAgreement}
            supplier={supplier}
            {...agreement}
          />
        </Route>
        <Redirect to={redirect} />
      </Switch>
    </div>
  )
}

SupplierProfile.propTypes = {
  address: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    county: PropTypes.string,
    postcode: PropTypes.string,
  }),
  agreement: PropTypes.shape({
    id: PropTypes.number,
    agreementProducts: PropTypes.array,
    deliveryTime: PropTypes.number,
    discount: PropTypes.string,
    document: PropTypes.string,
    minimumOrder: PropTypes.string,
    notes: PropTypes.string,
    orderCutoff: PropTypes.string,
    paymentTerms: PropTypes.number,
    status: PropTypes.oneOf(['requested', 'proposed', 'accepted']),
  }),
  canStar: PropTypes.bool,
  canMessage: PropTypes.bool,
  canApproveAgreement: PropTypes.bool,
  canRequestAgreement: PropTypes.bool,
  canLeaveReview: PropTypes.bool,
  awards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      year: PropTypes.number,
    })
  ),
  coverImage: PropTypes.string,
  deliveryArea: PropTypes.string,
  description: PropTypes.string,
  id: PropTypes.number,
  linkPrefix: PropTypes.string,
  logo: PropTypes.string,
  name: PropTypes.string,
  products: PropTypes.array,
  reviews: PropTypes.array,
  showPrices: PropTypes.bool,
  sustailability: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      document: PropTypes.string,
      expiry: PropTypes.string,
      logo: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  starred: PropTypes.bool,
  videoUrl: PropTypes.string,
}

SupplierProfile.defaultProps = {
  address: {},
  awards: [],
  canAddProducts: true,
  canStar: true,
  canMessage: true,
  canApproveAgreement: true,
  canRequestAgreement: true,
  canLeaveReview: true,
  linkPrefix: '',
  products: [],
  reviews: [],
  showPrices: true,
  starred: false,
  sustainability: [],
}

export default SupplierProfile
